import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Select } from '../../components/atoms/Select'
import { Spinner } from '../../components/atoms/Spinner'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import { ProductComponentAddModal } from './ProductComponentAddModal'
import { ProductComponentEditModal } from './ProductComponentEditModal'
import {
  useProductComponents,
  type ComponentRow
} from '../../../viewmodels/useProductComponents'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /products/components — Product Components screen. For a given parent
 * product, declare which other products compose it. Backed by the M:N
 * link table `product_components` (migration 0117).
 *
 * UX flow:
 *   1. Header product picker — every active product, with a small
 *      "(N components)" hint next to ones that already are bundles.
 *   2. Below: the picked parent's component list — thumbnail + name +
 *      qty + sort + remove per row.
 *   3. "Add component" button opens a modal: pick a component product
 *      (excludes self + already-linked + products that already have
 *      their own components — the depth-1 rule), set qty + sort.
 *
 * Depth-1 rule reminders surface inline whenever they bite:
 *   - if the picked parent is itself a component somewhere, the page
 *     blocks "Add component" with a helpful inline notice.
 */
export function ProductComponentsPage(): JSX.Element {
  const vm = useProductComponents()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.PRODUCTCOMPONENTS, 'add')
  const canEdit = can(SCREENCODES.PRODUCTCOMPONENTS, 'edit')
  const canDelete = can(SCREENCODES.PRODUCTCOMPONENTS, 'delete')

  const [addOpen, setAddOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ComponentRow | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const parentSelected = vm.selectedParentId !== null && vm.selectedParentId !== ''
  const parentIsAlsoComponent = vm.parentMeta?.is_already_component ?? false
  const addBlockedReason = parentIsAlsoComponent
    ? `"${vm.parentMeta?.name ?? ''}" is itself a component of another product. Depth-1 limit: a component product cannot also have components of its own.`
    : null

  const handleDelete = async (componentProductId: string): Promise<void> => {
    if (!window.confirm('Remove this component from the product?')) return
    setActionError(null)
    setPendingDeleteId(componentProductId)
    const res = await vm.deleteComponent(componentProductId)
    setPendingDeleteId(null)
    if (!res.ok) setActionError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-h2 text-charcoal">Product Components</h1>
          <p className="font-body text-body-sm text-text-secondary mt-1">
            For a given product, declare which other products compose it. A
            product can be a parent OR a component, not both.
          </p>
        </div>
        <label className="block">
          <span className="font-body text-body-xs uppercase tracking-wide text-text-secondary">
            Product
          </span>
          <Select
            value={vm.selectedParentId ?? ''}
            onChange={(e) => vm.setSelectedParentId(e.target.value === '' ? null : e.target.value)}
            className="mt-1 w-80"
          >
            <option value="">— Select a product —</option>
            {vm.parents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.active_component_count > 0
                  ? ` · ${p.active_component_count} component${p.active_component_count === 1 ? '' : 's'}`
                  : ''}
              </option>
            ))}
          </Select>
        </label>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={vm.reload}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}

      {!parentSelected ? (
        <Placeholder>Pick a product to manage its components.</Placeholder>
      ) : vm.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <>
          {addBlockedReason !== null ? (
            <Alert tone="warning">{addBlockedReason}</Alert>
          ) : null}

          <div className="flex items-center justify-between">
            <h2 className="font-heading text-h3 text-charcoal">
              Components of {vm.parentMeta?.name ?? ''}
            </h2>
            {canAdd ? (
              <Button
                onClick={() => setAddOpen(true)}
                disabled={addBlockedReason !== null}
              >
                Add component
              </Button>
            ) : null}
          </div>

          {vm.components.length === 0 ? (
            <Placeholder>
              No components yet. This product currently ships as a single unit.
            </Placeholder>
          ) : (
            <TableShell>
              <table className="w-full font-body text-body-sm">
                <thead className="bg-parchment/50 text-text-secondary">
                  <tr>
                    <Th className="w-12">Image</Th>
                    <Th>Component product</Th>
                    <Th className="text-right">Qty</Th>
                    <Th className="text-right">Sort</Th>
                    <Th className="w-32">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {vm.components.map((c) => (
                    <tr key={c.component_product_id} className="border-t border-border align-middle">
                      <Td>
                        {c.component_thumbnail_url !== null ? (
                          <img
                            src={c.component_thumbnail_url}
                            alt=""
                            className="w-10 h-10 object-cover rounded border border-border"
                          />
                        ) : (
                          <span className="text-text-secondary">—</span>
                        )}
                      </Td>
                      <Td className="text-charcoal">{c.component_product_name}</Td>
                      <Td className="text-right font-mono">{c.quantity}</Td>
                      <Td className="text-right font-mono text-text-secondary">{c.sort_order}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          {canEdit ? (
                            <button
                              type="button"
                              className="text-body-xs underline underline-offset-2 hover:text-charcoal"
                              onClick={() => setEditTarget(c)}
                            >
                              Edit
                            </button>
                          ) : null}
                          {canDelete ? (
                            <button
                              type="button"
                              className="text-body-xs underline underline-offset-2 text-rust hover:text-charcoal disabled:opacity-50"
                              disabled={pendingDeleteId === c.component_product_id}
                              onClick={() => void handleDelete(c.component_product_id)}
                            >
                              {pendingDeleteId === c.component_product_id ? 'Removing…' : 'Remove'}
                            </button>
                          ) : null}
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableShell>
          )}
        </>
      )}

      {addOpen ? (
        <ProductComponentAddModal
          open={addOpen}
          parentName={vm.parentMeta?.name ?? ''}
          picker={vm.picker}
          onClose={() => setAddOpen(false)}
          onSubmit={async (input) => {
            const r = await vm.addComponent(input)
            if (r.ok) setAddOpen(false)
            return r
          }}
        />
      ) : null}

      {editTarget !== null ? (
        <ProductComponentEditModal
          open={editTarget !== null}
          row={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={async (input) => {
            const patch: { componentProductId: string; quantity?: number; sortOrder?: number } = {
              componentProductId: editTarget.component_product_id
            }
            if (input.quantity !== undefined) patch.quantity = input.quantity
            if (input.sortOrder !== undefined) patch.sortOrder = input.sortOrder
            const r = await vm.updateComponent(patch)
            if (r.ok) setEditTarget(null)
            return r
          }}
        />
      ) : null}
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-body text-body-sm text-text-secondary">{children}</p>
    </div>
  )
}
